import React, { useEffect, useState, useCallback, useContext } from 'react';
import cx from 'classnames';
import A11yVisuallyHidden from 'components/a11y/a11y-visually-hidden';
import Icon from 'components/icon/icon';
import Link from 'components/link/link';
import Button from 'components/button/button';
import { Lexeme } from '@bumble/localization/components';
import LocaleContext from 'lib/locale-context';
import { getCookieSettings } from 'lib/cookies';
import { usePageVisibility } from 'lib/use-page-visibility';
import { addNewWindowLabelToAnchors } from 'components/typography/formatted-text';

type Props = {
    ctaLink?: string;
    icon?: React.ReactNode;
    hasCta?: boolean;
    title?: string | React.ReactNode;
    text?: string;
};

function CookieNotification(props: Props) {
    const isPageVisible = usePageVisibility();
    const lang = useContext(LocaleContext);

    const {
        ctaLink = `https://badoo.com/${lang}${'/cookie-policy'}`,
        icon = <Icon size="md" name={Icon.Name.Close} />,

        hasCta,
        title,
        text,
    } = props;

    const className = {
        block: cx({
            'cookie-notification': true,
            'cookie-notification--cta': hasCta,
        }),
        closeAction: cx({
            'cookie-notification__close': true,
        }),
    };

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const cookieConsentProvided = Boolean(getCookieSettings());
        const showCookieBanner = () => {
            setIsVisible(true);
            global.document.body.classList.add('show-cookie-notification');
        };
        const hideCookieBanner = () => {
            setIsVisible(false);
            global.document.body.classList.remove('show-cookie-notification');
        };

        // client only code
        if (global.window) {
            if (!cookieConsentProvided) {
                showCookieBanner();
            }

            if (isPageVisible && cookieConsentProvided) {
                hideCookieBanner();
            }
        }
    }, [isPageVisible]);

    const hideCookieBanner = useCallback(() => {
        setIsVisible(false);
        cookie('cookie_banner_closed', true, '', '/');
        global.document.body.classList.remove('show-cookie-notification');
    }, []);

    const hideCookieBannerWithAccept = useCallback(() => {
        setIsVisible(false);
        acceptAllCookies();
        global.document.body.classList.remove('show-cookie-notification');
    }, []);

    const formattedText = text ? addNewWindowLabelToAnchors(text) : null;

    return (
        <div className={className.block} style={{ display: isVisible ? 'block' : 'none' }}>
            <div className="cookie-notification__inner">
                {title ? <div className="cookie-notification__title">{title}</div> : null}

                <div
                    className="cookie-notification__text"
                    dangerouslySetInnerHTML={
                        formattedText
                            ? {
                                  __html: formattedText,
                              }
                            : undefined
                    }
                >
                    {text ? null : (
                        <>
                            <Lexeme path="settings@cookies.banner.title" />{' '}
                            <Link
                                className="cookie-notification__link"
                                href={ctaLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Lexeme path="settings@cookies.footer.cookie_policy" />

                                <A11yVisuallyHidden>
                                    <Lexeme path="mw@link.a11y.label.new-window" />
                                </A11yVisuallyHidden>
                            </Link>
                        </>
                    )}
                </div>

                {hasCta ? (
                    <div className="cookie-notification__actions">
                        <div className="cookie-notification__action">
                            <Button
                                designType={Button.designType.STROKE}
                                color={Button.color.White}
                                target="_blank"
                                href="https://badoo.com/cookie-policy#manage_your_cookies"
                                text={<Lexeme path="settings@cookies.banner.settings_cta" />}
                            />
                        </div>

                        <div className="cookie-notification__action">
                            <Button
                                designType={Button.designType.FILLED}
                                color={Button.color.White}
                                jsClass="js-cookie-accept-all"
                                text={<Lexeme path="settings@cookies.banner.accept_cta" />}
                                onClick={hideCookieBannerWithAccept}
                            />
                        </div>
                    </div>
                ) : null}

                {!hasCta ? (
                    <button className={className.closeAction} onClick={hideCookieBanner}>
                        <span className="cookie-notification__icon">{icon}</span>
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export default CookieNotification;

// images/-/moxie/new/js/components/cookie-banner.js
// eslint-disable-next-line max-params
function cookie(
    name: string,
    value?: any,
    expires?: Date | string,
    path?: string,
    domain?: string,
    secure?: any
): string | boolean | undefined {
    if (arguments.length === 1) {
        return global.document.cookie.match(new RegExp(`(^|; )${name}=(.*?)(;|$)`))
            ? decodeURIComponent(RegExp.$2)
            : undefined;
    }

    global.document.cookie = `${name}=${encodeURIComponent(value)}${
        expires ? `; expires=${expires instanceof Date ? expires.toUTCString() : expires}` : ''
    }${path ? `; path=${path}` : ''}${domain ? `; domain=${domain}` : ''}${
        secure ? '; secure' : ''
    }`;

    return cookie(name) === value;
}

// new cookie consent
function acceptAllCookies() {
    const cookies = { functional: true, analytics: true };
    const cookieSettings = encodeURIComponent(JSON.stringify(cookies));
    const expiryDate = new Date();
    expiryDate.setTime(Number(new Date()) + 1000 * 60 * 60 * 24 * 365);
    global.document.cookie = `cookie_settings=${cookieSettings}; expires=${expiryDate.toUTCString()}`;
}
